/**
 * Job Action Orchestration Layer
 * 
 * SINGLE AUTHORITATIVE PATH for all high-value job actions.
 * Each action: performs state update → emits canonical event → triggers automation → returns result.
 * 
 * Components should delegate to this layer instead of manually wiring
 * state mutations, event emission, and follow-up creation.
 */

import { useCallback } from "react";
import { logUserAction } from "@/lib/userActionLogger";
import { useAppData } from "@/data/AppDataContext";
import { useEventEmitter } from "@/hooks/useEventEmitter";
import { useRole } from "@/data/RoleContext";
import { validateStatusWrite } from "@/data/workflowAudit";
import { validateCloseoutGate, applyPaymentCollectionToJob, markPaymentDeferred } from "@/data/installerWrapEngine";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import { createInstallFollowUp } from "@/data/unifiedFollowUp";
import { recordPayment, createAuditEntry, type InvoiceAuditEntry } from "@/data/invoiceEngine";
import type { Job, Issue, IssueType, IssueSeverity, IssueBlockerCategory, IssueAffectedArea, PaymentMethod } from "@/data/types";
import { JobOperationsService } from "@/services/job-operations.service";
import { JobService } from "@/services/job.service";
import { AuditLogService } from "@/services/audit.service";

// ── Result contract ──

export interface ActionResult {
  success: boolean;
  error?: string;
  warnings?: string[];
  affectedJobId: string;
  emittedEventType?: string;
}

// ── Action input types ──

export interface CreateIssueData {
  jobId: string;
  issueType: IssueType;
  severity: IssueSeverity;
  description: string;
  owner?: string;
  photo?: string;
  source?: string;
  relatedContractorId?: string;
  blockerCategory?: IssueBlockerCategory;
  blockerDetail?: string;
  homeownerConcern?: string;
  affectedArea?: IssueAffectedArea;
  suggestedAction?: string;
  jobStageAtCreation?: string;
}

export interface RecordPaymentData {
  invoiceId: string;
  amount: number;
  method: PaymentMethod;
}

export interface CompleteQAData {
  passed: boolean;
  issueEncountered: boolean;
  issueNotes?: string;
  issuePhoto?: string;
  installerName: string;
}

export interface AdminOverrideData {
  action: string;
  reason: string;
  field?: string;
  value?: string;
}

// ── Hook ──

export function useJobActions() {
  const {
    jobs, setJobs,
    issues, setIssues,
    installerWrapRecords, setInstallerWrapRecords,
    installFollowUpTasks, setInstallFollowUpTasks,
  } = useAppData();
  const {
    emitWrapSubmitted,
    emitIssueCreated,
    emitQACompleted,
    emitPaymentCollected,
    emitInstallCompleted,
    emitAdminOverride,
  } = useEventEmitter();
  const { role } = useRole();

  // ═══════════════════════════════════════
  // A. submitWrap
  // ═══════════════════════════════════════

  const submitWrap = useCallback(async (job: Job, record: InstallerWrapRecord): Promise<ActionResult> => {
    const warnings: string[] = [];
    const persistedRecord = await JobOperationsService.createInstallerWrapRecord(job.id, record);

    if (!persistedRecord) {
      return {
        success: false,
        error: "Failed to persist installer wrap record",
        affectedJobId: job.id,
      };
    }

    // 1. Store wrap record
    setInstallerWrapRecords(prev => [persistedRecord, ...prev]);

    // Audit log
    await AuditLogService.create({
      actionType: "installer_wrap_submitted",
      entityType: "job",
      entityId: job.id,
      role: role,
      outcome: persistedRecord.closeoutEligible ? "success" : "blocked",
      metadata: {
        outcome: persistedRecord.outcome,
        completionPercent: persistedRecord.completionPercent,
        closeoutEligible: persistedRecord.closeoutEligible,
        paymentCapture: persistedRecord.paymentCapture?.disposition,
      },
    }).catch(() => {});

    // 2. Emit canonical event
    emitWrapSubmitted(job, persistedRecord.outcome, persistedRecord.completionPercent);

    if (persistedRecord.closeoutEligible) {
      // Closeout gate check
      const unresolvedCount = issues.filter(
        i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress")
      ).length;
      const gate = validateCloseoutGate(job, persistedRecord, unresolvedCount);

      if (!gate.canCloseout) {
        // Block closeout — annotate job
        setJobs(prev => prev.map(j => j.id === job.id ? {
          ...j,
          notes: `${j.notes ? j.notes + " | " : ""}[Closeout blocked ${new Date().toLocaleDateString()}] ${gate.failures.join("; ")}`.trim(),
        } : j));
        return {
          success: false,
          error: `Closeout blocked: ${gate.failures.join(", ")}`,
          affectedJobId: job.id,
          emittedEventType: "wrap_submitted",
        };
      }

      // Validate ownership
      const check = validateStatusWrite("installer", "currentStage", "QA Review");
      if (!check.valid) {
        warnings.push(`Ownership warning: ${check.violation}`);
      }

      // Apply financial sync
      setJobs(prev => prev.map(j => {
        if (j.id !== job.id) return j;
        let updated = j;
        if (persistedRecord.paymentCapture?.disposition === "collected") {
          updated = applyPaymentCollectionToJob(updated);
        } else if (persistedRecord.paymentCapture?.disposition === "deferred") {
          updated = markPaymentDeferred(updated);
        }
        return {
          ...updated,
          currentStage: "QA Review" as any,
          qaSubmitted: false,
          installStatus: "Install Complete",
          closeoutStatus: "Install Complete",
        };
      }));

      // Deferred payment follow-up
      await JobService.update(job.id, {
        currentStage: "QA Review" as any,
        qaSubmitted: false,
        installStatus: "Install Complete" as any,
        closeoutStatus: "Install Complete" as any,
      });

      if (persistedRecord.paymentCapture?.disposition === "deferred") {
        const followUp = createInstallFollowUp(
          { ...persistedRecord, outcome: "partial_reschedule" as any },
          installFollowUpTasks,
        );
        if (followUp) {
          const persistedTask = await JobOperationsService.createInstallFollowUpTask(job.id, {
            ...followUp,
            reason: "partial_reschedule",
            reasonLabel: `Payment Deferred - ${persistedRecord.paymentCapture?.deferralReason?.replace(/_/g, " ") ?? "unknown"}`,
          });
          setInstallFollowUpTasks(prev => [...prev, persistedTask ?? {
            ...followUp,
            reason: "partial_reschedule",
            reasonLabel: `Payment Deferred - ${persistedRecord.paymentCapture?.deferralReason?.replace(/_/g, " ") ?? "unknown"}`,
          }]);
          warnings.push("Payment follow-up task created");
        }
      }

      // Emit install completed
      emitInstallCompleted(job);

    } else {
      // Incomplete — annotate and create follow-up
      const wrapNote = `[Wrap ${new Date().toLocaleDateString()}] ${persistedRecord.outcome}: ${persistedRecord.progressNotes}`.trim();
      setJobs(prev => prev.map(j => j.id === job.id ? {
        ...j,
        notes: `${j.notes ? j.notes + " | " : ""}${wrapNote}`.trim(),
      } : j));
      await JobService.update(job.id, { notes: `${job.notes ? job.notes + " | " : ""}${wrapNote}`.trim() });

      const followUp = createInstallFollowUp(persistedRecord, installFollowUpTasks);
      if (followUp) {
        const persistedTask = await JobOperationsService.createInstallFollowUpTask(job.id, followUp);
        setInstallFollowUpTasks(prev => [...prev, persistedTask ?? followUp]);
        warnings.push("Follow-up task created for dispatch");
      }
    }

    logUserAction({ action: 'closeout_completed', entityId: job.id, route: '/jobs' });
    return {
      success: true,
      warnings: warnings.length > 0 ? warnings : undefined,
      affectedJobId: job.id,
      emittedEventType: "wrap_submitted",
    };
  }, [issues, installFollowUpTasks, setInstallerWrapRecords, setJobs, setInstallFollowUpTasks, emitWrapSubmitted, emitInstallCompleted]);

  // ═══════════════════════════════════════
  // B. createIssue
  // ═══════════════════════════════════════

  const createIssue = useCallback(async (data: CreateIssueData): Promise<ActionResult> => {
    const job = jobs.find(j => j.id === data.jobId);
    if (!job) {
      return { success: false, error: "Job not found", affectedJobId: data.jobId };
    }

    const newIssue: Issue = {
      id: `ISS-${String(issues.length + 1).padStart(3, "0")}`,
      jobId: data.jobId,
      issueType: data.issueType,
      severity: data.severity,
      description: data.description,
      photo: data.photo || "",
      owner: data.owner || "Unassigned",
      status: "Open" as const,
      dateCreated: new Date().toISOString().split("T")[0],
      source: (data.source || "Admin") as any,
      relatedContractorId: data.relatedContractorId,
      blockerCategory: data.blockerCategory,
      blockerDetail: data.blockerDetail,
      homeownerConcern: data.homeownerConcern,
      affectedArea: data.affectedArea,
      suggestedAction: data.suggestedAction,
      jobStageAtCreation: data.jobStageAtCreation as any,
    };

    setIssues(prev => [...prev, newIssue]);
    setJobs(prev => prev.map(j => j.id === data.jobId ? { ...j, issueFlag: true } : j));

    await AuditLogService.create({
      actionType: "issue_created",
      entityType: "issue",
      entityId: newIssue.id,
      role: role,
      outcome: "success",
      metadata: {
        issueType: newIssue.issueType,
        severity: newIssue.severity,
        owner: newIssue.owner,
        blockerCategory: newIssue.blockerCategory,
      },
    }).catch(() => {});

    emitIssueCreated(job, data.issueType, data.severity);

    logUserAction({ action: 'issue_created', entityId: data.jobId, route: '/issues' });
    return {
      success: true,
      affectedJobId: data.jobId,
      emittedEventType: "issue_created",
    };
  }, [jobs, issues, setIssues, setJobs, emitIssueCreated]);

  // ═══════════════════════════════════════
  // C. completeQA
  // ═══════════════════════════════════════

  const completeQA = useCallback(async (jobId: string, data: CompleteQAData): Promise<ActionResult> => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      return { success: false, error: "Job not found", affectedJobId: jobId };
    }

    // Update job state
    setJobs(prev => prev.map(j =>
      j.id === jobId ? {
        ...j,
        currentStage: "QA Review" as const,
        issueFlag: data.issueEncountered || j.issueFlag,
        qaSubmitted: true,
        qaStatus: "Submitted" as const,
        installStatus: "Install Complete" as const,
      } : j
    ));

    AuditLogService.create({
      actionType: "qa_completed",
      entityType: "job",
      entityId: jobId,
      role: role,
      outcome: data.passed ? "success" : "blocked",
      metadata: { passed: data.passed, installerName: data.installerName },
    }).catch(() => {});

    // Emit QA event
    emitQACompleted(job, data.passed);

    // Create issue if found during QA
    if (data.issueEncountered && data.issueNotes) {
      const issueResult = await createIssue({
        jobId,
        issueType: "Install Issue",
        severity: "Medium",
        description: data.issueNotes,
        owner: data.installerName,
        photo: data.issuePhoto,
        source: "QA Inspector",
      });
      if (!issueResult.success) {
        return {
          success: true,
          warnings: ["QA submitted but issue creation failed"],
          affectedJobId: jobId,
          emittedEventType: "qa_submitted",
        };
      }
    }

    logUserAction({ action: 'qa_completed', entityId: jobId, route: '/qa-form' });
    return {
      success: true,
      affectedJobId: jobId,
      emittedEventType: data.passed ? "qa_passed" : "qa_submitted",
    };
  }, [jobs, setJobs, emitQACompleted, createIssue]);

  // ═══════════════════════════════════════
  // D. recordPaymentAction
  // ═══════════════════════════════════════

  const recordPaymentAction = useCallback((
    job: Job,
    data: RecordPaymentData,
    onJobUpdate: (updated: Job) => void,
  ): ActionResult & { auditEntry?: InvoiceAuditEntry; updatedFinancials?: Job["financials"] } => {
    const result = recordPayment(job, data.invoiceId, data.amount, data.method);
    if (!result) {
      return { success: false, error: "Payment recording failed", affectedJobId: job.id };
    }

    const invoices = job.financials?.invoices ?? [];
    const payments = job.financials?.payments ?? [];
    const updatedInvoices = invoices.map(i => i.id === data.invoiceId ? result.updatedInvoice : i);
    const updatedPayments = [...payments, result.payment];

    const updatedFinancials: Job["financials"] = {
      ...job.financials!,
      invoices: updatedInvoices,
      payments: updatedPayments,
    };

    onJobUpdate({
      ...job,
      financials: updatedFinancials,
    });

    const auditEntry = createAuditEntry(
      "payment_recorded",
      `$${data.amount.toLocaleString()} received for ${data.invoiceId}`,
      { invoiceId: data.invoiceId, paymentId: result.payment.id },
    );

    emitPaymentCollected(job, data.amount, data.method);

    AuditLogService.create({
      actionType: "payment_recorded",
      entityType: "job",
      entityId: job.id,
      role: role,
      outcome: "success",
      metadata: {
        invoiceId: data.invoiceId,
        amount: data.amount,
        method: data.method,
        paymentId: result.payment.id,
      },
    }).catch(() => {});

    logUserAction({ action: 'payment_recorded', entityId: job.id, route: '/accounting' });
    return {
      success: true,
      affectedJobId: job.id,
      emittedEventType: "payment_collected",
      auditEntry,
      updatedFinancials,
    };
  }, [emitPaymentCollected]);

  // ═══════════════════════════════════════
  // E. completeInstall
  // ═══════════════════════════════════════

  const completeInstall = useCallback((job: Job): ActionResult => {
    const check = validateStatusWrite("installer", "installStatus", "Install Complete");
    const warnings: string[] = [];
    if (!check.valid) {
      warnings.push(`Ownership warning: ${check.violation}`);
    }

    setJobs(prev => prev.map(j =>
      j.id === job.id ? {
        ...j,
        installStatus: "Install Complete" as any,
      } : j
    ));

    AuditLogService.create({
      actionType: "install_completed",
      entityType: "job",
      entityId: job.id,
      role: role,
      outcome: "success",
    }).catch(() => {});

    emitInstallCompleted(job);

    logUserAction({ action: 'install_completed', entityId: job.id, route: '/jobs' });
    return {
      success: true,
      warnings: warnings.length > 0 ? warnings : undefined,
      affectedJobId: job.id,
      emittedEventType: "install_completed",
    };
  }, [setJobs, emitInstallCompleted]);

  // ═══════════════════════════════════════
  // F. applyAdminOverride
  // ═══════════════════════════════════════

  const applyAdminOverrideAction = useCallback((job: Job, data: AdminOverrideData): ActionResult => {
    if (!data.reason || data.reason.trim().length < 3) {
      return { success: false, error: "Override reason is required", affectedJobId: job.id };
    }

    // Apply the override to job notes for audit trail
    setJobs(prev => prev.map(j =>
      j.id === job.id ? {
        ...j,
        notes: `${j.notes ? j.notes + " | " : ""}[Admin Override ${new Date().toLocaleDateString()}] ${data.action}: ${data.reason}`.trim(),
      } : j
    ));

    AuditLogService.create({
      actionType: "admin_override",
      entityType: "job",
      entityId: job.id,
      role: role,
      outcome: "overridden",
      reason: data.reason,
      metadata: { action: data.action, field: data.field, value: data.value?.toString() },
    }).catch(() => {});

    emitAdminOverride(job, data.action, data.reason);

    logUserAction({ action: 'admin_override', entityId: job.id, meta: { overrideAction: data.action } });
    return {
      success: true,
      affectedJobId: job.id,
      emittedEventType: "admin_override",
    };
  }, [setJobs, emitAdminOverride]);

  return {
    submitWrap,
    createIssue,
    completeQA,
    recordPaymentAction,
    completeInstall,
    applyAdminOverrideAction,
  };
}
